import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel,
  MenuItem, Select, Typography, Paper, IconButton, Checkbox,
  Tooltip, TextField, Button, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "../../../config/axiosConfig";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useSearchParams } from "react-router-dom";
import ServicioAvatar from "../../../components/ServicioAvatar";
import { useAuth } from "../../../context/AuthContext";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  whiteSpace: "nowrap",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 36, fontSize: "0.82rem" },
  "& .MuiInputBase-input": { py: "8px" },
  "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  "& .MuiSelect-select": { py: "8px" },
};

const estadosOT = {
  0: "Eliminado",
  1: "Emitido",
  2: "Detalle",
  3: "Asignado",
  4: "Procesando",
  5: "Entregado",
  6: "Concluido",
  7: "Finalizado",
  8: "Todos",
  9: "Pendientes",
};

const estadoColor = {
  0: "default",
  1: "warning",
  2: "info",
  3: "primary",
  4: "primary",
  5: "info",
  6: "success",
  7: "success",
  8: "error",
};

const TablaOrdenesTrabajo = ({ defaultSoloMias = false, ocultarCheckboxMias = false }) => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState(9);
  const [buscarTexto, setBuscarTexto] = useState("");
  const [buscar, setBuscar] = useState("");
  // defaultSoloMias: para embeber esta tabla ya filtrada (ej. la pestaña
  // "Mis Pendientes"). ?mias=1: acceso directo desde el dashboard.
  const [soloMias, setSoloMias] = useState(() => defaultSoloMias || searchParams.get("mias") === "1");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [total, setTotal] = useState(0);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [otAEliminar, setOtAEliminar] = useState(null);
  const [observacionEliminar, setObservacionEliminar] = useState("");
  const navigate = useNavigate();

  const fetchOrdenes = useCallback(async (opts = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        buscar,
        order: "desc",
        page: opts.page ?? page,
        limit: opts.pageSize ?? pageSize,
      };
      if (estadoFiltro !== 8) params.estado = estadoFiltro; // 8 = Todos
      // "Solo mis OT": las que creó el usuario O donde figura como técnico
      // asignado (nom_tecnico se guarda por nombre, no username).
      if (soloMias && user?.username) params.id_usu = user.username;
      if (soloMias && user?.nombre) params.nombre_tecnico = user.nombre;
      const { data } = await axios.get("/api/ot-solicitudes", { params });
      const payload = data?.data ?? data;
      setOrdenes(Array.isArray(payload?.items) ? payload.items : []);
      setTotal(Number(payload?.total || 0));
      setPage(Number(payload?.page || params.page));
      setPageSize(Number(payload?.limit || params.limit));
    } catch (e) {
      setOrdenes([]);
      setTotal(0);
      setError(e?.response?.data?.message || "Error al cargar las órdenes de trabajo.");
    } finally {
      setLoading(false);
    }
  }, [buscar, estadoFiltro, page, pageSize, soloMias, user?.username, user?.nombre]);

  useEffect(() => {
    fetchOrdenes({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoFiltro, buscar, soloMias, user?.username, user?.nombre]);

  const handleSubmitBusqueda = (e) => {
    e.preventDefault();
    setBuscar(buscarTexto.trim());
  };

  const handleLimpiar = () => {
    setBuscarTexto("");
    setBuscar("");
    setEstadoFiltro(9);
    setSoloMias(defaultSoloMias);
  };

  const abrirDialogEliminar = (o) => {
    if (o.tiene_equipos || eliminandoId) return;
    setObservacionEliminar("");
    setOtAEliminar(o);
  };

  const cerrarDialogEliminar = () => {
    if (eliminandoId) return;
    setOtAEliminar(null);
  };

  const confirmarEliminar = async () => {
    const obs = observacionEliminar.trim();
    if (!otAEliminar || !obs) return;
    setEliminandoId(otAEliminar.id);
    setError("");
    try {
      await axios.delete(`/api/ot-solicitudes/${otAEliminar.id}`, { data: { observacion: obs } });
      setOtAEliminar(null);
      await fetchOrdenes();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo eliminar la OT.");
    } finally {
      setEliminandoId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Órdenes de Trabajo
      </Typography>

      <Box component="form" onSubmit={handleSubmitBusqueda} sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Cliente o código de OT..."
          value={buscarTexto}
          onChange={(e) => setBuscarTexto(e.target.value)}
          sx={{ ...compactFieldSx, minWidth: 220 }}
        />
        <FormControl size="small" sx={{ ...compactFieldSx, minWidth: 170 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={estadoFiltro}
            label="Estado"
            onChange={(e) => setEstadoFiltro(parseInt(e.target.value, 10))}
          >
            {Object.entries(estadosOT).map(([key, label]) => (
              <MenuItem key={key} value={parseInt(key, 10)}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" size="small" variant="contained" startIcon={<SearchIcon />} sx={{ minHeight: 36 }}>
          Buscar
        </Button>
        <Button size="small" variant="outlined" color="secondary" onClick={handleLimpiar} sx={{ minHeight: 36 }}>
          Limpiar
        </Button>
        {!ocultarCheckboxMias && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={soloMias}
                onChange={(e) => setSoloMias(e.target.checked)}
              />
            }
            label="Solo mis OT"
            sx={{ ml: 0.5, "& .MuiFormControlLabel-label": { fontSize: "0.82rem" } }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
          Total: {total}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Paper variant="outlined">
        <TableContainer>
          <Table
            size="small"
            sx={{ "& .MuiTableCell-root": { px: 0.75, py: 0.45, fontSize: "0.78rem" } }}
          >
            <TableHead>
              <TableRow>
                <StyledHeaderCell>ID</StyledHeaderCell>
                <StyledHeaderCell>Cliente</StyledHeaderCell>
                <StyledHeaderCell>Tipo de O.T.</StyledHeaderCell>
                <StyledHeaderCell>Servicio</StyledHeaderCell>
                <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                <StyledHeaderCell>Técnico(s)</StyledHeaderCell>
                <StyledHeaderCell>Creado por</StyledHeaderCell>
                <StyledHeaderCell>Registro</StyledHeaderCell>
                <StyledHeaderCell align="center">Acciones</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : ordenes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ color: "text.secondary", fontStyle: "italic", py: 2 }}>
                    No se encontraron órdenes de trabajo.
                  </TableCell>
                </TableRow>
              ) : (
                ordenes.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell>{o.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <ServicioAvatar imagen={o.imagen_principal} nombre={o.razon_social} size={24} />
                        <span>{o.razon_social}</span>
                      </Box>
                    </TableCell>
                    <TableCell>{o.nombre_ot_tipo}</TableCell>
                    <TableCell>{o.tipo_servicio}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={estadosOT[o.estado] || "Desconocido"}
                        color={estadoColor[o.estado] || "default"}
                        sx={{ height: 20, fontSize: "0.68rem" }}
                      />
                    </TableCell>
                    <TableCell>{o.nom_tecnico || "-"}</TableCell>
                    <TableCell>{o.id_usu || "-"}</TableCell>
                    <TableCell>{o.fecha_registro}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Detalle">
                        <IconButton color="info" size="small" onClick={() => navigate(`/orden-de-trabajo/ot/${o.id}`)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={o.tiene_equipos ? "No se puede eliminar: tiene equipos asignados" : "Eliminar OT"}>
                        <span>
                          <IconButton
                            color="error"
                            size="small"
                            disabled={Boolean(o.tiene_equipos) || eliminandoId === o.id}
                            onClick={() => abrirDialogEliminar(o)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={Math.max(page - 1, 0)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 30, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          onPageChange={(_, newPage) => fetchOrdenes({ page: newPage + 1 })}
          onRowsPerPageChange={(e) => fetchOrdenes({ page: 1, pageSize: Number(e.target.value) })}
          sx={{ minHeight: 40, "& .MuiTablePagination-toolbar": { minHeight: 40, px: 1 } }}
        />
      </Paper>

      <Dialog open={!!otAEliminar} onClose={cerrarDialogEliminar} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar OT #{otAEliminar?.id}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            La OT pasará a estado "Eliminado". Indica el motivo.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Observación"
            value={observacionEliminar}
            onChange={(e) => setObservacionEliminar(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogEliminar} disabled={!!eliminandoId}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmarEliminar}
            disabled={!observacionEliminar.trim() || !!eliminandoId}
          >
            {eliminandoId ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaOrdenesTrabajo;
